import styled from '@emotion/styled'
import { calcRem, breakpointMax } from 'component-library/__styling/style-functions'
import { colors, fonts, easing } from 'component-library/__styling/settings'
import { WebNavLink, WebLinkProps, WebNavLinkProps } from 'common/navigation/web-link'
import { IconUser } from 'component-library/icons/IconUser/IconUser'
import { IconPremium } from 'component-library/icons/IconPremium/IconPremium'
import { conditionalStyles } from 'component-library/__styling/conditional-styles'
import { StyledComponent } from '@emotion/styled'
import { createPropFilter } from 'common/emotion'
import { Skeleton } from 'component-library/content/Skeleton/Skeleton'

export const subscribeButtonClassname = 'subscribe-button'
export const usernameButtonClassname = 'username-button'

export const StyledActionLinks = styled('div')(({ theme }) => ({
    display: 'flex',
    fontFamily: fonts.thewest.sansSerif,

    '> *:not(:last-child)::after': {
        position: 'absolute',
        right: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        content: `''`,
        height: calcRem(20),
        borderRight: `1px solid ${colors.thewest.greyDolphin}`,
    },

    // Need to hide the psuedo element when a regional site is active
    // as the return link gets hidden in StyledReturnLink after the sm breakpoint
    ...conditionalStyles(theme.section !== 'default', {
        [breakpointMax('sm')]: {
            [`.${subscribeButtonClassname}`]: {
                '::after': {
                    content: 'none',
                },
            },
        },
    }),

    '> *:last-child': {
        paddingRight: 0,
    },
}))

export interface ActionLinkProps {
    isPrimary: boolean
    strokeHover?: boolean
}

const linkVertPadding = 13

export const StyledReturnLink = styled(WebNavLink, {
    shouldForwardProp: createPropFilter<WebNavLinkProps>()(['isPrimary', 'strokeHover']),
})<ActionLinkProps>({
    position: 'relative',
    padding: calcRem(linkVertPadding / 2, 8),
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    fontSize: calcRem(12),
    fontWeight: 600,
    textDecoration: 'none',

    [breakpointMax('sm')]: {
        display: 'none',
    },
})

export const StyledActionLink = styled(WebNavLink, {
    shouldForwardProp: createPropFilter<WebLinkProps>()(['isPrimary', 'strokeHover']),
})<ActionLinkProps>(
    {
        position: 'relative',
        padding: calcRem(linkVertPadding / 2, 8),
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        fontSize: calcRem(14),
        fontWeight: 700,
        textDecoration: 'none',
        textTransform: 'uppercase',

        '&:hover, &:focus': {
            outline: 'none',
            textDecoration: 'underline',
            color: colors.thewest.westblue,
        },

        [`&.${usernameButtonClassname}`]: {
            padding: calcRem(0, 0, 0, 2),
            textDecoration: 'underline',
        },
    },
    ({ isPrimary, strokeHover, theme }) => ({
        color: isPrimary ? theme.sectionValues.primaryColor : colors.thewest.greyGorilla,
        ...conditionalStyles(strokeHover, {
            '&:hover svg': {
                stroke: colors.thewest.greyGorilla,
            },
        }),
    })
)

export const StyledActionLinkSkeleton = StyledActionLink.withComponent('span') as StyledComponent<
    ActionLinkProps
>

export const StyledActionText = styled('p')({
    position: 'relative',
    padding: calcRem(linkVertPadding / 2, 8),
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    fontSize: calcRem(14),
    fontWeight: 700,
    textDecoration: 'none',
    textTransform: 'uppercase',
    color: colors.thewest.greyGorilla,
    margin: 0,
})

export const StyledActionTextUsername = styled(StyledActionText)({
    [breakpointMax('xs')]: {
        display: 'none',
    },
})

export interface ActionButtonProps {
    isPrimary?: boolean
}

export const StyledActionButton = styled('button')<ActionButtonProps>(({ theme, isPrimary }) => ({
    position: 'relative',
    padding: calcRem(linkVertPadding / 2, 8),
    whiteSpace: 'nowrap',
    border: 0,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: calcRem(14),
    fontWeight: 700,
    lineHeight: 1,
    textDecoration: 'none',
    textTransform: 'uppercase',
    transition: `color ${easing.easeOut.fast}, background ${easing.easeOut.fast}, boxShadow ${easing.easeOut.fast}`,
    fontFamily: theme.fonts.sansSerif,
    color: isPrimary ? theme.sectionValues.primaryColor : colors.thewest.greyGorilla,

    '&:hover': {
        textDecoration: 'underline',
    },

    '&:focus, &.is-active': {
        textDecoration: 'underline',
    },
}))

export const StyledPremiumIcon = styled(IconPremium)({
    width: calcRem(17),
    height: calcRem(17),
    verticalAlign: 'text-top',
    marginRight: calcRem(4),
})

export const StyledIconUser = styled(IconUser)(({ theme }) => ({
    height: calcRem(20),
    width: calcRem(20),
    marginLeft: calcRem(8),
    stroke: theme.sectionValues.primaryColor,
}))

export const StyledSkeleton = styled(Skeleton)({
    display: 'inline-block',
    width: calcRem(60),
})
